import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { safetyGateService, SafetyGateSeverity, type SafetyGateDto, type SafetyGateFilters } from '../services/safetyGateService';
import { useNotificationStore } from '../store/notificationStore';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

const severityColor = (severity: SafetyGateSeverity) => {
  switch (severity) {
    case SafetyGateSeverity.Low:
      return 'success' as const;
    case SafetyGateSeverity.Medium:
      return 'warning' as const;
    case SafetyGateSeverity.High:
      return 'error' as const;
    case SafetyGateSeverity.Critical:
      return 'error' as const;
    default:
      return 'default' as const;
  }
};

/**
 * Safety gates list page component.
 */
export const SafetyGatesListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [gates, setGates] = useState<SafetyGateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<SafetyGateFilters>({
    pageNumber: 1,
    pageSize: 20,
    searchTerm: '',
    severity: undefined,
    isActive: undefined,
  });

  const loadGates = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await safetyGateService.getSafetyGates(filters);
      setGates(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load safety gates');
      addNotification('Failed to load safety gates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGates();
  }, [filters.pageNumber, filters.pageSize, filters.severity, filters.isActive, filters.searchTerm]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this safety gate?')) {
      return;
    }

    try {
      await safetyGateService.deleteSafetyGate(id);
      addNotification('Safety gate deleted successfully', 'success');
      loadGates();
    } catch (err: any) {
      addNotification(err.message || 'Failed to delete safety gate', 'error');
    }
  };

  const handleToggleActive = async (gate: SafetyGateDto) => {
    try {
      await safetyGateService.updateSafetyGate(gate.id, {
        ...gate,
        isActive: !gate.isActive,
      });
      addNotification(`Safety gate ${gate.isActive ? 'deactivated' : 'activated'} successfully`, 'success');
      loadGates();
    } catch (err: any) {
      addNotification(err.message || 'Failed to update safety gate', 'error');
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<SafetyGateDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (gate) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {gate.name}
          </Typography>
          {gate.description && (
            <Typography variant="caption" color="text.secondary">
              {gate.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'actionPattern',
      label: 'Action Pattern',
      minWidth: 200,
      render: (gate) => (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.875rem">
          {gate.actionPattern}
        </Typography>
      ),
    },
    {
      id: 'severity',
      label: 'Severity',
      minWidth: 120,
      render: (gate) => (
        <Chip label={gate.severity} color={severityColor(gate.severity)} size="small" />
      ),
    },
    {
      id: 'requiresApproval',
      label: 'Requires Approval',
      minWidth: 150,
      render: (gate) =>
        gate.requiresApproval ? (
          <Chip label="Yes" color="primary" size="small" />
        ) : (
          <Chip label="No" size="small" />
        ),
    },
    {
      id: 'requiredApprovers',
      label: 'Required Approvers',
      minWidth: 150,
      render: (gate) => gate.requiredApprovers || '-',
    },
    {
      id: 'autoApprovalTimeoutMinutes',
      label: 'Auto-Approval Timeout',
      minWidth: 180,
      render: (gate) => (gate.autoApprovalTimeoutMinutes ? `${gate.autoApprovalTimeoutMinutes} min` : '-'),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      render: (gate) => (
        <FormControlLabel
          control={
            <Switch
              checked={gate.isActive}
              onChange={() => handleToggleActive(gate)}
              size="small"
            />
          }
          label={gate.isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'right',
      render: (gate) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/safety-gates/${gate.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(gate.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / (filters.pageSize || 20));

  return (
    <PageListLayout
      title="Safety Gates"
      createButton={{
        label: 'Create Safety Gate',
        onClick: () => navigate('/safety-gates/new'),
        icon: <AddIcon />,
      }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="Search safety gates"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={filters.severity || ''}
              label="Severity"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  severity: (e.target.value as SafetyGateSeverity) || undefined,
                  pageNumber: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={SafetyGateSeverity.Low}>Low</MenuItem>
              <MenuItem value={SafetyGateSeverity.Medium}>Medium</MenuItem>
              <MenuItem value={SafetyGateSeverity.High}>High</MenuItem>
              <MenuItem value={SafetyGateSeverity.Critical}>Critical</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.isActive === undefined ? '' : filters.isActive.toString()}
              label="Status"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  isActive: e.target.value === '' ? undefined : e.target.value === 'true',
                  pageNumber: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={!loading && gates.length === 0}
      emptyState={{
        title: 'No safety gates found',
        description: 'Create a new safety gate to get started.',
        action: {
          label: 'Create Safety Gate',
          onClick: () => navigate('/safety-gates/new'),
        },
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages,
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={gates}
      />
    </PageListLayout>
  );
};
